import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date");

export const fetchQuestionChapters = createServerFn({ method: "POST" }).handler(async () => {
  const { requireProfile } = await import("./session.server");
  const { listQuestionChapters } = await import("./questions.server");
  const profile = await requireProfile();
  return listQuestionChapters(profile.id);
});

export const saveQuestionTarget = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({ chapterId: z.string().uuid(), target: z.number().int().min(10).max(2000) })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { updateChapterTarget } = await import("./questions.server");
    const profile = await requireProfile();
    await updateChapterTarget(profile.id, data.chapterId, data.target);
    return { ok: true };
  });

export const createQuestionSetFn = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        subject: z.string().min(1).max(40),
        chapter: z.string().min(1).max(120),
        topic: z.string().max(120).optional().nullable(),
        source: z.enum([
          "PYQ",
          "Module",
          "NCERT",
          "Coaching Sheet",
          "Mock/Test",
          "Practice",
          "Other",
        ]),
        target: z.number().int().min(1).max(5000).optional().nullable(),
        scheduleMode: z.enum(["auto", "self"]),
        today: dateSchema,
        self: z
          .object({
            startDate: dateSchema,
            gapDays: z.number().int().min(1).max(30),
            perSession: z.number().int().min(1).max(500),
            endDate: dateSchema.optional().nullable(),
          })
          .optional()
          .nullable(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { createQuestionSet } = await import("./questions.server");
    const profile = await requireProfile();
    return createQuestionSet(profile.id, data, data.today);
  });

export const rescheduleQuestionSession = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ taskId: z.string().uuid(), date: dateSchema }).parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { rescheduleQuestionTask } = await import("./questions.server");
    const profile = await requireProfile();
    await rescheduleQuestionTask(profile.id, data.taskId, data.date);
    return { ok: true };
  });

export const completeQuestionPractice = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        taskId: z.string().uuid(),
        today: dateSchema,
        solved: z.number().int().min(0).max(500).optional(),
        attempted: z.number().int().min(0).max(500).optional(),
        correct: z.number().int().min(0).max(500).optional(),
        incorrect: z.number().int().min(0).max(500).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { completeQuestionTask } = await import("./questions.server");
    const profile = await requireProfile();
    return completeQuestionTask(profile.id, data.taskId, data.today, data.solved, {
      attempted: data.attempted,
      correct: data.correct,
      incorrect: data.incorrect,
    });
  });

export const removeQuestionChapter = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => z.object({ chapterId: z.string().uuid() }).parse(input))
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { deleteQuestionChapter } = await import("./questions.server");
    const profile = await requireProfile();
    await deleteQuestionChapter(profile.id, data.chapterId);
    return { ok: true };
  });